import { type QueryFunctionContext, useQuery } from "@tanstack/react-query";
import { fallbackToZero, type PerformanceWithAccountFilter } from "features/Library/Common/utils/performanceUtils";
import { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import { type LicenseAccountRow } from "features/Reporting/types/packs";
import { type LicensesAccountPackRow } from "features/Reporting/Packs/Pack/types";
import {
  getLicensesEngagement,
  fetchLicensesAccounts,
  fetchLicensesAccountPacks,
} from "features/Reporting/Packs/services/packsLicensingReportingService";

import { type FetchPageFn } from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";

export const createLicensesAccountPacksFetcher = (
  filter: PerformanceWithAccountFilter,
): FetchPageFn<LicensesAccountPackRow> => {
  return (pagination, signal) => fetchLicensesAccountPacks(filter, pagination, signal);
};

type LicensesEngagementQueryContext = QueryFunctionContext<[_: string, filter: PerformanceWithAccountFilter]>;

const getLicensesEngagementQuery = async ({ queryKey, signal }: LicensesEngagementQueryContext) => {
  const [, filter] = queryKey;
  const { data } = await getLicensesEngagement(filter, signal);

  const packsCards: CardReportingItem[] = [
    {
      statDescription: "Customers",
      stat: data.Customers,
      popupBody: "Total number of customers.",
    },
    {
      statDescription: "Free Packs",
      stat: data.FreePacks,
      popupBody: "Total number of free packs enabled for customers.",
    },
    {
      statDescription: "Other Packs",
      stat: fallbackToZero(data.OtherPacks),
      popupBody: "Total number of other packs enabled for customers.",
    },
    {
      statDescription: "Paid Packs",
      stat: fallbackToZero(data.PaidPacks),
      popupBody: "Total number of paid packs enabled for customers.",
    },
    {
      statDescription: "Trial Packs",
      stat: data.TrialPacks,
      popupBody: "Total number of trial packs enabled for customers.",
    },
    {
      statDescription: "Total Packs",
      stat: data.TotalPacks,
      popupBody: "Total number of packs (regardless of type) enabled for customers.",
    },
    {
      statDescription: "Avg. Packs/Customer",
      stat: data.AveragePacks,
      popupBody: "Average number of packs enabled for customers.",
    },
  ];

  const licensesCards: CardReportingItem[] = [
    {
      statDescription: "Total Packs",
      stat: data.TotalPacks,
      popupBody: "Total number of packs (regardless of type) enabled for this customer.",
    },
    {
      statDescription: "Licenses Issued",
      stat: data.LicensesIssued,
      popupBody: "Total number of licenses issued across all packs.",
    },
    {
      statDescription: "Contracted Licenses",
      stat: data.LicensesContracted,
      popupBody: "Total number of licenses contracted across all packs.",
    },
    {
      statDescription: "License Usage",
      stat: data.PctLicensesUtilized * 100,
      isStatPercentage: true,
      popupBody: "Percentage of licenses issued out of the total number of licenses contracted.",
    },
  ];

  return {
    packsCards,
    licensesCards,
  };
};

export const useLicensesEngagementQuery = (filter: PerformanceWithAccountFilter) =>
  useQuery({
    queryKey: ["LicensesEngagement", filter],
    queryFn: getLicensesEngagementQuery,
    enabled: filter.type !== undefined,
  });

export const createLicensesAccountsFetcher = (filter: PerformanceWithAccountFilter): FetchPageFn<LicenseAccountRow> => {
  return (pagination, signal) => fetchLicensesAccounts(filter, pagination, signal);
};
